import React, { type FC, useMemo, useRef } from "react";
import type { FormHandle } from "@src/components/form";
import { BusinessForm, type BusinessFormRenderProps } from "@src/components/business-form";
import { OrderSearchSelect, WsDepotCardPickerField } from "@src/components/business-data-entry";
import { measureEndDecorator } from "@src/helpers/business/measureEndDecorator";
import FormFooter from "@shared/ui/form-footer";
import { DetailList, DetailRow } from "@shared/ui/detail-row";
import { FormPanel } from "@shared/ui/form-panel";
import { useWsDepotCardResolveQuery, useWsInplantContentDetailQuery } from "../../hooks/useWsInplantContentDetailQuery";
import { BalanceDocType } from "@src/types/business/balanceDocType";
import type { ApiWsInplantContent, WsInplantContent } from "../../../../../../api/wsInplants";
import type { UseWsInplantContentMutationsResult } from "../../useWsInplantContentMutations";
import {
    wsInplantContentFormConfig,
    type WsInplantContentFormContext,
    type WsInplantContentFormValues,
} from "./wsInplantContent.form-config";
import "./WsInplantContentForm.scss";
import { NumberField } from "@omega/ui";

/** Пропсы overlay-окна элемента состава ВЗН. */
export interface WsInplantContentFormProps {
    mode: "create" | "edit";
    contentCode?: number;
    inplantCode: number;
    inplantNum: string;
    docType: BalanceDocType;
    readonly: boolean;
    senderDivisionCode?: number;
    mutations: UseWsInplantContentMutationsResult;
}

function createInitialValues(): WsInplantContentFormValues {
    return {
        leaveCardCode: null,
        leaveQty: 0,
        arrivalQty: 0,
        orderCode: null,
    };
}

function toApiPayload(values: WsInplantContentFormValues, inplantCode: number, contentCode: number): ApiWsInplantContent {
    return {
        Code: contentCode,
        InplantCode: inplantCode,
        LeaveCardCode: values.leaveCardCode ?? 0,
        LeaveQty: values.leaveQty,
        ArrivalQty: values.arrivalQty,
        OrderCode: values.orderCode ?? 0,
        ArrivalCardCode: 0,
        ArrivalCardType: 0,
        ArticleOfExpense: 0,
        LotCode: 0,
        LeaveSdbCode: 0,
        ArrivalSdbCode: 0,
    };
}

/**
 * Создание и редактирование элемента состава ВЗН УП.
 */
export const WsInplantContentForm: FC<WsInplantContentFormProps> = ({
    mode,
    contentCode,
    inplantCode,
    // inplantNum,
    docType,
    readonly,
    senderDivisionCode,
    mutations,
}) => {
    const contentQuery = useWsInplantContentDetailQuery({ mode, contentCode, inplantCode });

    const loadedContent = contentQuery.data;

    const formContext = useMemo(
        (): WsInplantContentFormContext => ({
            docType,
            readonly,
            senderDivisionCode,
        }),
        [docType, readonly, senderDivisionCode],
    );

    const boundFormConfig = useMemo(() => wsInplantContentFormConfig.withContext(formContext), [formContext]);

    const initialValues = useMemo((): WsInplantContentFormValues | undefined => {
        if (mode === "create") {
            return createInitialValues();
        }
        if (loadedContent == null) {
            return undefined;
        }
        return boundFormConfig.toForm(loadedContent);
    }, [mode, loadedContent, boundFormConfig]);

    const handleSubmit = async (values: WsInplantContentFormValues): Promise<void> => {
        const payload = toApiPayload(values, inplantCode, mode === "edit" && contentCode != null ? contentCode : 0);

        if (mode === "create") {
            await mutations.createMutation.mutateAsync(payload);
            return;
        }

        await mutations.updateMutation.mutateAsync(payload);
    };

    const formApiRef = useRef<FormHandle<WsInplantContentFormValues> | null>(null);
    const isReady = mode === "create" || (initialValues != null && !contentQuery.isFetching);

    return (
        <FormPanel
            disabled={readonly}
            formApiRef={formApiRef}
            useDefaultFooter={!readonly}
            formProps={{
                formConfig: boundFormConfig,
                initialValues,
                ready: isReady,
                onSubmit: handleSubmit,
            }}
            footer={
                !readonly
                    ? ({ submitForm, isFormSubmitting }) => (
                          <FormFooter
                              confirmText={mode === "create" ? "Добавить" : "Сохранить"}
                              confirmLoading={isFormSubmitting}
                              onConfirm={submitForm}
                          />
                      )
                    : undefined
            }
        >
            {(form) => (
                <WsInplantContentFields
                    form={form}
                    formContext={formContext}
                    senderDivisionCode={senderDivisionCode}
                    loadedLeaveCard={loadedContent?.LeaveCard}
                />
            )}
        </FormPanel>
    );
};

interface WsInplantContentFieldsProps {
    form: BusinessFormRenderProps<WsInplantContentFormValues>;
    formContext: WsInplantContentFormContext;
    senderDivisionCode?: number;
    loadedLeaveCard?: WsInplantContent["LeaveCard"];
}

function WsInplantContentFields({ form, formContext, senderDivisionCode, loadedLeaveCard }: WsInplantContentFieldsProps) {
    const leaveCardCode = form.getValues("leaveCardCode");

    const leaveCardQuery = useWsDepotCardResolveQuery(leaveCardCode);

    const leaveCard = leaveCardQuery.data ?? loadedLeaveCard;
    const measure = leaveCard?.Measure;

    return (
        <>
            <BusinessForm.Field name="leaveCardCode">
                <WsDepotCardPickerField
                    value={form.getValues("leaveCardCode")}
                    onChange={(cardCode) => {
                        form.setValue("leaveCardCode", cardCode, { shouldDirty: true });
                    }}
                    label={form.field("leaveCardCode").getUiProps().label}
                    required={form.field("leaveCardCode").getUiProps().required}
                    disabled={form.field("leaveCardCode").getUiProps().disabled}
                    error={form.getFieldState("leaveCardCode").error != null}
                    flt={senderDivisionCode != null ? { DivisionCode: senderDivisionCode } : undefined}
                />
            </BusinessForm.Field>

            {leaveCard != null && (
                <DetailList className="ws_inplant_content_form__depot_info">
                    <DetailRow label="Обозначение" value={leaveCard.Stockobj?.Sign ?? ""} />
                    <DetailRow label="Наименование" value={leaveCard.Stockobj?.Name ?? ""} />
                    <DetailRow label="Заводской код" value={leaveCard.Stockobj?.NomNum ?? ""} />
                    <DetailRow label="Тип продукции" value={leaveCard.ProductionType?.Name ?? ""} />
                </DetailList>
            )}

            <BusinessForm.Row columns={2}>
                <BusinessForm.Field name="leaveQty">
                    <NumberField
                        {...form.field("leaveQty").getInputProps()}
                        endAdornment={measureEndDecorator(measure)}
                        onValueChange={(value) => {
                            form.setValue("leaveQty", value ?? 0, { shouldDirty: true });
                            // Если это выдача, то устанавливаем получено также
                            if (formContext.docType === BalanceDocType.Leave) {
                                form.setValue("arrivalQty", value ?? 0, { shouldDirty: true });
                            }
                        }}
                    />
                </BusinessForm.Field>
                <BusinessForm.Field name="arrivalQty">
                    <NumberField
                        {...form.field("arrivalQty").getInputProps()}
                        endAdornment={measureEndDecorator(measure)}
                        onValueChange={(value) => {
                            form.setValue("arrivalQty", value ?? 0, { shouldDirty: true });
                        }}
                    />
                </BusinessForm.Field>
            </BusinessForm.Row>

            <BusinessForm.Field name="orderCode">
                <OrderSearchSelect
                    disabled={formContext.readonly}
                    {...form.field("orderCode").getSearchSelectProps((order) => order?.Code ?? null)}
                    fullscreenTitle="Выберите Заказ"
                />
            </BusinessForm.Field>
        </>
    );
}
